import { GQL } from "@binale-tech/shared";

import { withThinking } from "../runner/runAgent";
import type { AgentStep, ReportLine } from "../runner/types";
import { isInYear } from "./inYear";

// Mirrors the server cap of bankingTransactionsMatchContacts
const MATCH_BATCH = 5000;

export const bankContactsStep: AgentStep = {
    id: "bankContacts",
    product: [GQL.IProductKey.Banks, GQL.IProductKey.Contacts],
    async run(ctx, io) {
        const ids = ctx
            .bankTransactions()
            .filter((tx) => !tx.contactId && isInYear(tx.bookingDate, ctx.year))
            .map((tx) => String(tx.id));
        if (ids.length === 0) return { status: "nothing" };

        const matches = await withThinking(io, "app.agent.bankContacts.thinking", async () => {
            const all: GQL.ITransactionContactMatch[] = [];
            for (let i = 0; i < ids.length; i += MATCH_BATCH) {
                all.push(...(await ctx.matchBankContacts(ids.slice(i, i + MATCH_BATCH))));
            }
            return all;
        });
        if (
            matches.length > 0 &&
            matches.every((m) => m.outcome === GQL.ITransactionContactMatchOutcome.NoContactsProduct)
        ) {
            io.post({ kind: "warning", textId: "app.agent.bankContacts.noContactsProduct" });
            return { status: "skipped" };
        }

        const matched = matches.filter((m) => m.outcome === GQL.ITransactionContactMatchOutcome.Matched).length;
        const linkedIds = new Set(
            matches
                .filter(
                    (m) =>
                        m.outcome === GQL.ITransactionContactMatchOutcome.Matched ||
                        m.outcome === GQL.ITransactionContactMatchOutcome.AlreadyLinked,
                )
                .map((m) => String(m.transactionId)),
        );
        if (matched > 0) {
            io.post({ kind: "done", textId: "app.agent.bankContacts.matched", values: { count: matched } });
        }

        const rest = ids.filter((id) => !linkedIds.has(id));
        const suggestions =
            rest.length > 0
                ? await withThinking(io, "app.agent.bankContacts.suggesting", () =>
                      ctx.readBankContactSuggestions(rest),
                  )
                : [];

        const summary: ReportLine[] = [];
        const open: ReportLine[] = [];
        if (matched > 0) summary.push({ textId: "app.agent.report.bankContactsMatched", values: { count: matched } });

        if (suggestions.length > 0) {
            const answer = await io.ask(
                "app.agent.bankContacts.ask",
                [
                    { value: "create", labelId: "app.agent.bankContacts.create", primary: true },
                    { value: "skip", labelId: "app.agent.bankContacts.skip" },
                ],
                { count: suggestions.length },
            );
            if (answer === "create") {
                const results = await withThinking(io, "app.agent.bankContacts.creating", () =>
                    ctx.createBankContacts(
                        suggestions.map((s) => s.vatId),
                        rest,
                    ),
                );
                const count = (outcome: GQL.IBankContactSuggestionOutcome) =>
                    results.filter((r) => r.outcome === outcome).length;
                const created = count(GQL.IBankContactSuggestionOutcome.CreatedAndLinked);
                const existing = count(GQL.IBankContactSuggestionOutcome.LinkedToExisting);
                if (created > 0) {
                    io.post({ kind: "done", textId: "app.agent.bankContacts.created", values: { count: created } });
                    summary.push({ textId: "app.agent.report.bankContactsCreated", values: { count: created } });
                }
                if (existing > 0) {
                    summary.push({
                        textId: "app.agent.report.bankContactsLinkedExisting",
                        values: { count: existing },
                    });
                }
            } else {
                open.push({ textId: "app.agent.report.bankContactsOpen", values: { count: suggestions.length } });
            }
        }

        return { status: summary.length > 0 || open.length > 0 ? "done" : "nothing", summary, open };
    },
};
